/**
 * Alta y edición de producto. Un solo formulario para los dos casos: los
 * campos son los mismos y `ProductIn` y `ProductPatch` sólo difieren en que en
 * el segundo todo es opcional.
 *
 * El GTIN se valida **mientras se escribe** contra el espejo de `gs1.py`
 * (`gtin.ts`) y se muestra normalizado a 14 dígitos antes de enviar, para que
 * quien pega un EAN-13 de una caja vea qué es lo que va a quedar guardado.
 * La autoridad sigue siendo la API: si igual devuelve 422, se muestra su
 * mensaje con el `trace_id`.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import type { Schemas } from '@/api/types';
import type { Translate } from '@/i18n/translate';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { SECURITY_TIERS, TIER_LABEL_KEYS, isSecurityTier, type SecurityTier } from './catalog';
import { normalizeGtin, type GtinResult } from './gtin';
import { SelectField, TextField } from './ui/fields';

type Product = Schemas['ProductOut'];

export interface ProductDraft {
  sku: string;
  name: string;
  /** Ya normalizado a 14 dígitos, o `null` si el campo quedó vacío. */
  gtin: string | null;
  security_tier: SecurityTier;
  is_active: boolean;
}

export interface ProductFormProps {
  /** `undefined` = alta. */
  product?: Product;
  onSubmit: (draft: ProductDraft) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  /** Error de la mutación, si la hubo. */
  error: unknown;
}

const ACTIVE_VALUES = ['true', 'false'] as const;

export function ProductForm({
  product,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: ProductFormProps): ReactNode {
  const { t } = useI18n();
  const [sku, setSku] = useState(product?.sku ?? '');
  const [name, setName] = useState(product?.name ?? '');
  const [gtin, setGtin] = useState(product?.gtin ?? '');
  const [tier, setTier] = useState<SecurityTier>(
    product !== undefined && isSecurityTier(product.security_tier)
      ? product.security_tier
      : 'qr_scratch',
  );
  const [isActive, setIsActive] = useState(product?.is_active ?? true);
  // Los errores de campo aparecen recién al primer intento de envío: marcar en
  // rojo un SKU que todavía se está tipeando es ruido, no ayuda.
  const [touched, setTouched] = useState(false);

  const gtinResult = normalizeGtin(gtin);
  const gtinError = gtinMessage(gtinResult, t);
  const skuError = touched && sku.trim() === '' ? t('form.required') : undefined;
  const nameError = touched && name.trim() === '' ? t('form.required') : undefined;
  const canSubmit = sku.trim() !== '' && name.trim() !== '' && gtinResult.ok;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setTouched(true);
    // `canSubmit` incluye `gtinResult.ok`, así que después de esta guarda
    // TypeScript ya sabe que `gtinResult.value` existe.
    if (!canSubmit) return;
    onSubmit({
      sku: sku.trim(),
      name: name.trim(),
      gtin: gtinResult.value,
      security_tier: tier,
      is_active: isActive,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <TextField
        label={t('products.field.sku')}
        hint={t('products.field.sku.hint')}
        value={sku}
        onChange={setSku}
        error={skuError}
        required
        maxLength={64}
        mono
        placeholder="AMOX-500"
      />

      <TextField
        label={t('products.field.name')}
        value={name}
        onChange={setName}
        error={nameError}
        required
        maxLength={200}
        placeholder="Amoxicilina 500 mg x 21 cápsulas"
      />

      <TextField
        label={t('products.field.gtin')}
        hint={
          gtinResult.ok && gtinResult.value !== null && gtinResult.value !== gtin
            ? t('products.gtin.normalized', { gtin: gtinResult.value })
            : t('products.field.gtin.hint')
        }
        value={gtin}
        onChange={setGtin}
        error={gtinError}
        maxLength={20}
        mono
        placeholder="7891234567895"
      />

      <SelectField
        label={t('products.field.tier')}
        hint={t('products.field.tier.hint')}
        value={tier}
        onChange={(value) => {
          if (isSecurityTier(value)) setTier(value);
        }}
        options={SECURITY_TIERS.map((value) => ({ value, label: t(TIER_LABEL_KEYS[value]) }))}
      />

      <SelectField
        label={t('products.field.active')}
        hint={t('products.field.active.hint')}
        value={isActive ? 'true' : 'false'}
        onChange={(value) => {
          setIsActive(value === ACTIVE_VALUES[0]);
        }}
        options={[
          { value: 'true', label: t('products.active') },
          { value: 'false', label: t('products.inactive') },
        ]}
      />

      {Boolean(error) && <ErrorState error={error} />}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : t('form.save')}
        </Button>
      </div>
    </form>
  );
}

/** Traduce el problema del GTIN a la frase que se muestra bajo el campo. */
function gtinMessage(result: GtinResult, t: Translate): string | undefined {
  if (result.ok) return undefined;
  switch (result.problem) {
    case 'notDigits':
      return t('products.gtin.notDigits');
    case 'length':
      return t('products.gtin.length', { length: result.length });
    case 'checkDigit':
      return t('products.gtin.checkDigit', { expected: result.expected });
  }
}
